import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

export interface FormChange {
  name: string;
  value: any;
  isValid: boolean;
}

interface FormContextValue {
  form: Record<string, any>;
  validity: Record<string, boolean>;
  handleChange: (data: FormChange) => void;
  reset: boolean;
}

export const FormContext = createContext<FormContextValue | null>(null);

export const useFormContext = () => {
  const context = useContext(FormContext);

  if (!context) {
    throw new Error("useFormContext must be used inside a Form");
  }

  return context;
};

export interface FormProps {
  children: React.ReactNode;
  onSubmit: (form: any) => void;
  className?: string;
}

export function Form({ onSubmit, children, className }: FormProps) {
  const [form, setForm] = useState<Record<string, any>>({});
  const [validity, setValidity] = useState<Record<string, boolean>>({});
  const [reset, setReset] = useState(false);

  const handleChange = useCallback(({ name, value, isValid }: FormChange) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setValidity((prev) => ({
      ...prev,
      [name]: isValid,
    }));
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const isFormValid = Object.values(validity).every(
        (value) => value === true,
      );

      console.log("Form.tsx handleSubmit called. isFormValid:", isFormValid, "validity:", validity);

      if (!isFormValid) {
        return;
      }

      onSubmit(form);
    },
    [form, onSubmit, validity],
  );

  const handleReset = useCallback(() => {
    setReset(true);

    setTimeout(() => {
      setReset(false);
    }, 0);
  }, []);

  const contextValue = useMemo(
    () => ({
      form,
      validity,
      handleChange,
      reset,
    }),
    [form, validity, handleChange, reset],
  );

  return (
    <FormContext.Provider value={contextValue}>
      <form className={className} onSubmit={handleSubmit} onReset={handleReset}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

export default Form;
